import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchMenuFromDb } from '../../assets/lib/api/menuApi';
import { useAuth } from '../contexts/AuthContext';
import { localizeMenuTree } from '../utils/comm/menuLocalized';

/**
 * 메뉴 데이터를 로드하는 커스텀 훅
 * 전역 캐시를 사용하여 중복 호출을 방지합니다.
 *
 * @param {Object} options - 옵션 객체
 * @param {string} options.rootMenuCd - 루트 메뉴 코드 (예: 'BASIC', 'ALLIANCE')
 * @param {string} options.fallbackJsonPath - 폴백 JSON 파일 경로
 * @param {boolean} options.skip - true일 경우 API 호출을 건너뜀
 * @returns {Object} { menuData, menuLoading }
 */
const useMenuData = (options = {}) => {
  const { rootMenuCd, fallbackJsonPath = '/assets/data/menus/top-menu.json', skip = false } = options;
  const [rawMenuData, setRawMenuData] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const { i18n } = useTranslation();
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || false;

  const menuData = useMemo(
    () => localizeMenuTree(rawMenuData, i18n.language),
    [rawMenuData, i18n.language]
  );

  // 캐시 키 생성 (rootMenuCd 및 인증 여부 포함)
  const authSuffix = isAuthenticated ? '_AUTH' : '_GUEST';
  const cacheKey = rootMenuCd ? `__MENU_DATA_${rootMenuCd}${authSuffix}_I18N__` : `__MENU_DATA${authSuffix}_I18N__`;
  const loadingKey = rootMenuCd ? `__MENU_LOADING_${rootMenuCd}${authSuffix}_I18N__` : `__MENU_LOADING${authSuffix}_I18N__`;

  useEffect(() => {
    // skip이 true이면 API 호출을 건너뜀
    if (skip) {
      setRawMenuData([]);
      setMenuLoading(false);
      return;
    }

    const fetchMenu = async () => {
      // 1. 전역 캐시(메모리) 확인
      if (window[cacheKey]) {
        if (Array.isArray(window[cacheKey]) && window[cacheKey].length > 0) {
          setRawMenuData(window[cacheKey]);
          setMenuLoading(false);
          return;
        }
      }

      // 2. 세션 스토리지 확인 (새로고침 대응)
      try {
        const SessionMenuData = sessionStorage.getItem(cacheKey);
        if (SessionMenuData) {
          const parsedData = JSON.parse(SessionMenuData);
          if (Array.isArray(parsedData) && parsedData.length > 0) {
            window[cacheKey] = parsedData; // 메모리 캐시에도 올려둠
            setRawMenuData(parsedData);
            setMenuLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn('sessionStorage 파싱 에러 (메뉴 데이터)', e);
      }

      // 로딩 중인 경우 대기
      if (window[loadingKey]) {
        const checkInterval = setInterval(() => {
          if (window[cacheKey]) {
            setRawMenuData(window[cacheKey]);
            setMenuLoading(false);
            clearInterval(checkInterval);
          } else if (!window[loadingKey]) {
            // 로딩 실패한 경우
            setMenuLoading(false);
            clearInterval(checkInterval);
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }

      setMenuLoading(true);
      window[loadingKey] = true;

      try {
        const data = await fetchMenuFromDb({ rootMenuCd });
        if (data && data.menu && data.menu.length > 0) {
          window[cacheKey] = data.menu; // 메모리 캐시 저장
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(data.menu)); // 세션 스토리지 저장
          } catch (e) { }
          setRawMenuData(data.menu);
        } else {
          // DB가 정상이지만 메뉴 데이터가 비어있을 경우 예외 발생시켜 catch 블록 타게 유도
          throw new Error('Empty menu data from DB');
        }
      } catch (error) {
        console.error('Failed to load menu data from DB (or empty data):', error);
        // DB 조회 실패 또는 빈 데이터 시 폴백하지 않고 빈 배열 처리 (top-menu.json 샘플 사용 안 함)
        setRawMenuData([]);
      } finally {
        window[loadingKey] = false;
        setMenuLoading(false);
      }
    };
    fetchMenu();
  }, [rootMenuCd, cacheKey, loadingKey, fallbackJsonPath, skip, isAuthenticated]);

  return { menuData, menuLoading };
};

export default useMenuData;

